import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCategories } from '../../contexts/CategoriesContext';

/**
 * Editorial top-nav: max-width scroll list with thin chevron arrows
 * that sit OUTSIDE the menu text area (no overlap), plus a soft cream
 * fade-mask on each edge so the cut-off categories taper out gracefully.
 */
const DesktopCategoryNav = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const { getParentCategories, loading } = useCategories();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const containerRef = useRef(null);

  const categories = getParentCategories();

  const checkScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    const hasOverflow = scrollWidth > clientWidth + 4;
    setShowLeftArrow(scrollLeft > 4);
    setShowRightArrow(hasOverflow && scrollLeft < scrollWidth - clientWidth - 4);
  }, []);

  useEffect(() => {
    if (categories.length > 0 && containerRef.current) {
      const id = window.setTimeout(checkScroll, 80);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [categories, checkScroll]);

  useEffect(() => {
    const handleResize = () => checkScroll();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkScroll]);

  const scroll = (direction) => {
    if (!containerRef.current) return;
    const amount = 240;
    containerRef.current.scrollTo({
      left:
        direction === 'left'
          ? containerRef.current.scrollLeft - amount
          : containerRef.current.scrollLeft + amount,
      behavior: 'smooth',
    });
    window.setTimeout(checkScroll, 300);
  };

  if (categories.length === 0 || loading) return null;

  const arrowStyle = {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    color: 'var(--ink)',
    opacity: 0.55,
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    flexShrink: 0,
  };

  return (
    <div
      className="flex items-center"
      style={{ maxWidth: '680px', margin: '0 auto', gap: '8px' }}
    >
      {/* Left chevron — ALWAYS rendered (visibility toggled) so layout never shifts */}
      <button
        type="button"
        onClick={() => scroll('left')}
        aria-label="Scroll categories left"
        style={{
          ...arrowStyle,
          visibility: showLeftArrow ? 'visible' : 'hidden',
          pointerEvents: showLeftArrow ? 'auto' : 'none',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.55')}
      >
        <ChevronLeft strokeWidth={1.2} style={{ width: 16, height: 16 }} />
      </button>

      {/* Edge-fade scroll area */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          // Soft cream tapers on each edge so cut-off labels don't read as broken
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%)',
          maskImage:
            'linear-gradient(to right, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%)',
        }}
      >
        <div
          ref={containerRef}
          onScroll={checkScroll}
          className="flex items-center"
          style={{
            gap: '28px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            scrollBehavior: 'smooth',
            padding: '4px 16px',
          }}
        >
          {/* "Todos" — link to /shop showing every product */}
          {(() => {
            const isAllActive = location.pathname === '/shop';
            return (
              <Link
                key="__all__"
                to="/shop"
                data-testid="nav-all-products"
                className={`nav-cat-link ${isAllActive ? 'nav-cat-link--active' : ''}`}
                style={{ fontWeight: 600 }}
              >
                {t('nav.all', 'Todos')}
                <span className="nav-cat-underline" />
              </Link>
            );
          })()}
          {categories.map((category) => {
            let displayName = category.slug;
            if (category.versions && category.versions.length > 0) {
              const firstVersion =
                category.versions.find((v) => v.name) || category.versions[0];
              displayName = firstVersion?.name || category.slug;
            } else if (category.name) {
              const currentLang = i18n.language || 'es';
              displayName =
                category.name?.[currentLang] || category.name?.es || category.name;
            }

            const isActive = location.pathname === `/shop/${category.slug}`;

            return (
              <Link
                key={category.slug}
                to={`/shop/${category.slug}`}
                className={`nav-cat-link ${isActive ? 'nav-cat-link--active' : ''}`}
              >
                {displayName}
                <span className="nav-cat-underline" />
              </Link>
            );
          })}
        </div>
      </div>

      {/* Right chevron */}
      <button
        type="button"
        onClick={() => scroll('right')}
        aria-label="Scroll categories right"
        style={{
          ...arrowStyle,
          visibility: showRightArrow ? 'visible' : 'hidden',
          pointerEvents: showRightArrow ? 'auto' : 'none',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.55')}
      >
        <ChevronRight strokeWidth={1.2} style={{ width: 16, height: 16 }} />
      </button>

      {/* Hover + active underline — CSS class avoids the inline-style override
          that previously prevented Tailwind's group-hover from animating width. */}
      <style>{`
        .nav-cat-link {
          position: relative;
          flex-shrink: 0;
          white-space: nowrap;
          font-family: var(--font-body);
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.4px;
          color: var(--ink);
          text-decoration: none;
          padding: 6px 0;
        }
        .nav-cat-underline {
          position: absolute;
          left: 0;
          bottom: 0;
          height: 1px;
          width: 0;
          background: var(--terracotta);
          transition: width 280ms ease-out;
        }
        .nav-cat-link:hover .nav-cat-underline,
        .nav-cat-link--active .nav-cat-underline { width: 100%; }
      `}</style>
    </div>
  );
};

export default DesktopCategoryNav;
